/*    
<summary>
  This functional component "AddCustomerManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik } from "formik";
import _ from "lodash";
import moment from "moment";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { DATE_FORMAT, integerRegex } from "../../../constants/constants";
import { ageGroupOptions } from "../../../constants/options.constant";
import { IOption } from "../../../models/ICommon";
import FormikDateFieldMUI from "../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import FormikFormCheckBox from "../../../shared-components/FormikFormHandlers/FormikFormCheckBox";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputForRegexCheck from "../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import FormikFormInputMobileNumber from "../../../shared-components/FormikFormHandlers/FormikFormInputMobileNumber";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import { advanceSearchValidationSchema } from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import LoaderButton from "../../../shared-components/button/loader-button";
import Label from "../../../shared-components/label/label";
import { toHalfWidth } from "../../../helpers/validate-helper";

interface IProps {
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  getDataLoading: boolean;
  initialValues: any;
  allCustomerList: IOption[];
  motivationOptions: IOption[];
  agreementOptions: IOption[];
  actionLoading: boolean;
}

const AdvanceSearchCustomerForm = (props: IProps) => {
  const {} = props;

  return (
    <>
      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            <FormattedMessage id="advance_search" />
            <button
              onClick={props.onModalClosed}
              disabled={props.getDataLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize={true}
          validationSchema={advanceSearchValidationSchema}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            let tempValues = {
              ...values,
              VisitStartDate:
                values.IsVisitCantBe || values.IsVisitCanBe
                  ? values.VisitStartDate || ""
                  : "",
              VisitEndDate:
                values.IsVisitCantBe || values.IsVisitCanBe
                  ? values.VisitEndDate || ""
                  : "",
              LastVisitStartDate:
                values.IsLastVisitCantBe || values.IsLastVisitCanBe
                  ? values.LastVisitStartDate || ""
                  : "",
              LastVisitEndDate:
                values.IsLastVisitCantBe || values.IsLastVisitCanBe
                  ? values.LastVisitEndDate || ""
                  : "",
              NumberOfVisit: toHalfWidth(values.NumberOfVisit),
              NumberOfVisitTo: toHalfWidth(values.NumberOfVisitTo),
            };
            if(tempValues.VisitStartDate && tempValues.VisitEndDate === "")
              tempValues.VisitEndDate = moment(new Date()).format(DATE_FORMAT);
            if(tempValues.LastVisitStartDate && tempValues.LastVisitEndDate === "")
              tempValues.LastVisitEndDate = moment(new Date()).format(DATE_FORMAT);
            props.submitHandler({ ...tempValues });
          }}
        >
          {({ setErrors, values, validateForm, setFieldValue }) => {
            
            const handleCheckIsVisitCanBe = async (event: React.ChangeEvent<HTMLInputElement>) => {
              const field = event.target.name;
              const isChecked = event.target.checked;

              const errors = await validateForm();
              setErrors(errors);

              if (field === "IsVisitCanBe") {
                if (isChecked) {
                  setFieldValue("IsVisitCanBe", true);
                  setFieldValue("IsVisitCantBe", false); // Uncheck the other checkbox
                } else {
                  setFieldValue("IsVisitCanBe", false);
                }
              } else if (field === "IsVisitCantBe") {
                if (isChecked) {
                  setFieldValue("IsVisitCantBe", true);
                  setFieldValue("IsVisitCanBe", false); // Uncheck the other checkbox
                } else {
                  setFieldValue("IsVisitCantBe", false);
                }
              }
            };
            
            const handleCheckIsLastVisitCanBe = async (event: React.ChangeEvent<HTMLInputElement>) => {
              const field = event.target.name;
              const isChecked = event.target.checked;

              const errors = await validateForm();
              setErrors(errors);

              if (field === "IsLastVisitCanBe") {
                if (isChecked) {
                  setFieldValue("IsLastVisitCanBe", true);
                  setFieldValue("IsLastVisitCantBe", false); // Uncheck the other checkbox
                } else {
                  setFieldValue("IsLastVisitCanBe", false);
                }
              } else if (field === "IsLastVisitCantBe") {
                if (isChecked) {
                  setFieldValue("IsLastVisitCantBe", true);
                  setFieldValue("IsLastVisitCanBe", false); // Uncheck the other checkbox
                } else {
                  setFieldValue("IsLastVisitCantBe", false);
                }
              }
            };
            return (
              <Form className="user-form">
                <Modal.Body>
                  {/* Row 1: Last Name, First Name */}
                  <div className="row">
                    <div className="col-12 col-md-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="last_name"
                      />
                      <Field
                        name="LastName"
                        type="text"
                        className="form-control"
                        as={FormikFormInput}
                        placeholder="enter_last_name"
                      />
                    </div>
                    <div className="col-12 col-md-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="first_name"
                      />
                      <Field
                        name="FirstName"
                        type="text"
                        className="form-control"
                        as={FormikFormInput}
                        placeholder="enter_first_name"
                      />
                    </div>

                    <div className="col-12 d-flex align-items-center">
                      <Label className="mb-0 me-2" label="first_visit" />
                      <Field
                        className="mx-1"
                        name="IsVisitCanBe"
                        type="checkbox"
                        id="visit_can_be"
                        checked={values.IsVisitCanBe}
                        onChange={handleCheckIsVisitCanBe}
                      />
                      <label className="me-3" htmlFor="visit_can_be">
                        <FormattedMessage id="can_be" />
                      </label>
                      <Field
                        className="mx-1"
                        name="IsVisitCantBe"
                        type="checkbox"
                         id="visit_cant_be"
                        checked={values.IsVisitCantBe}
                        onChange={handleCheckIsVisitCanBe}
                      />
                      <label htmlFor="visit_cant_be">
                        <FormattedMessage id="cant_be" />
                      </label>
                    </div>

                    <div className="col-12">
                      <div className="input-group date-range-input">
                        <i className="bi bi-arrow-right-short date-range-icon"></i>
                        <div className="start-date col-6  form-group">
                          <Field
                            name="VisitStartDate"
                            as={FormikDateFieldMUI}
                            disableFuture={true}
                            disabled={
                              !(values.IsVisitCantBe || values.IsVisitCanBe)
                            }
                          />
                        </div>
                        <div className="end-date col-6  form-group">
                          <Field
                            name="VisitEndDate"
                            as={FormikDateFieldMUI}
                            disableFuture={true}
                            disabled={
                              !(values.IsVisitCantBe || values.IsVisitCanBe)
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-12 d-flex align-items-center">
                      <Label className="mb-0 me-2" label="last_visit" />
                      <Field
                        className="mx-1"
                        name="IsLastVisitCanBe"
                        type="checkbox"
                        id="last_visit_can_be"
                        checked={values.IsLastVisitCanBe}
                        onChange={handleCheckIsLastVisitCanBe}
                      />
                      <label className="me-3" htmlFor="last_visit_can_be">
                        <FormattedMessage id="can_be" />
                      </label>
                      <Field
                        className="mx-1"
                        name="IsLastVisitCantBe"
                        type="checkbox"
                        id="last_visit_cant_be"
                        checked={values.IsLastVisitCantBe}
                        onChange={handleCheckIsLastVisitCanBe}
                      />
                      <label htmlFor="last_visit_cant_be">
                        <FormattedMessage id="cant_be" />
                      </label>
                    </div>

                    <div className="col-12">
                      <div className="input-group date-range-input">
                        <i className="bi bi-arrow-right-short date-range-icon"></i>
                        <div className="start-date col-6  form-group">
                          <Field
                            name="LastVisitStartDate"
                            as={FormikDateFieldMUI}
                            disableFuture={true}
                            disabled={
                              !(
                                values.IsLastVisitCantBe ||
                                values.IsLastVisitCanBe
                              )
                            }
                          />
                        </div>
                        <div className="end-date col-6  form-group">
                          <Field
                            name="LastVisitEndDate"
                            as={FormikDateFieldMUI}
                            disableFuture={true}
                            disabled={
                              !(
                                values.IsLastVisitCantBe ||
                                values.IsLastVisitCanBe
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-12 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="phone1"
                      />
                      <Field
                        name="Phone1"
                        type="text"
                        className="form-control"
                        placeholder="enter_mobile_number"
                        regex={integerRegex}
                        as={FormikFormInputMobileNumber}
                        dontAddZeroAtStart={true}
                      />
                    </div>
                    <div className="col-12">
                      <Label
                        className="label-style"
                        required={false}
                        label="dob"
                      />
                      <div className="input-group date-range-input">
                        <i className="bi bi-arrow-right-short date-range-icon"></i>
                        <div className="col-6 start-date form-group">
                          <Field
                            name="Dob"
                            as={FormikDateFieldMUI}
                            disableFuture={true}
                          />
                        </div>
                        <div className="col-6 end-date form-group">
                          <Field
                            name="EndDOB"
                            as={FormikDateFieldMUI}
                            disableFuture={true}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="age_group"
                      />
                      <Field
                        name="AgeGroup"
                        as={FormikFormSelect}
                        options={ageGroupOptions}
                      />
                    </div>
                    <div className="col-sm-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="agreement"
                      />
                      <Field
                        className="form-select"
                        name="AgreementId"
                        options={props.agreementOptions}
                        as={FormikFormSelect}
                      />
                    </div>
                    <div className="col-6 form-group">
                      <Label
                        className="label-style"
                        label="gender"
                        required={false}
                      />
                      <div className="row">
                        <div className="col-sm-4">
                          <Field
                            className="form-check-input"
                            name="IsMale"
                            type="checkbox"
                            id="is_male"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_male"
                          >
                            <FormattedMessage id="male" />
                          </label>
                        </div>
                        <div className="col-sm-4">
                          <Field
                            className="form-check-input"
                            name="IsFemale"
                            type="checkbox"
                            id="is_female"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_female"
                          >
                            <FormattedMessage id="female" />
                          </label>
                        </div>
                        <div className="col-sm-4">
                          <Field
                            className="form-check-input"
                            name="IsNotSet"
                            type="checkbox"
                            id="is_not_set"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_not_set"
                          >
                            <FormattedMessage id="not_set" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-6 form-group">
                      <Label
                        className="label-style"
                        label="advance_search_reservation_label"
                        required={false}
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="IsReservation"
                            type="radio"
                            value="0"
                            id="can_be"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="can_be"
                          >
                            <FormattedMessage id="can_be" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="IsReservation"
                            type="radio"
                            value="1"
                            id="cant_be"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="cant_be"
                          >
                            <FormattedMessage id="cant_be" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-6 form-group">
                      <Label
                        className="label-style"
                        label="nomination_fee"
                        required={false}
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="IsNominationFeeCanBe"
                            type="checkbox"
                            id="is_nomination_fee_can_be"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_nomination_fee_can_be"
                          >
                            <FormattedMessage id="can_be" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="IsNominationFeeCantBe"
                            type="checkbox"
                            id="is_nomination_fee_cant_be"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_nomination_fee_cant_be"
                          >
                            <FormattedMessage id="cant_be" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-6 form-group">
                      <Label
                        className="label-style"
                        label="referer_details"
                        required={false}
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="IsRefererCanBe"
                            type="checkbox"
                            id="is_referer_can_be"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_referer_can_be"
                          >
                            <FormattedMessage id="can_be" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="IsRefererCantBe"
                            type="checkbox"
                            id="is_referer_cant_be"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_referer_cant_be"
                          >
                            <FormattedMessage id="cant_be" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-6 form-group">
                      <Label
                        className="label-style"
                        label="dm"
                        required={false}
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="IsDMYes"
                            type="checkbox"
                            id="is_dm_yes"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_dm_yes"
                          >
                            <FormattedMessage id="yes" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="IsDMNo"
                            type="checkbox"
                            id="is_dm_no"
                          />
                          <label
                            className="form-check-label ms-2"
                            htmlFor="is_dm_no"
                          >
                            <FormattedMessage id="no" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 form-group">
                      <Label className="label-style" label="no_of_visit" />
                     <div className="d-flex align-items-center">
                      <div className="input-group me-2 w-auto">
                        <Field
                          name="NumberOfVisit"
                          type="text"
                          className="form-control"
                          regex={integerRegex}
                          as={FormikFormInputForRegexCheck}
                          placeholder="from"
                        />
                        <span className="input-group-text"><FormattedMessage id="label_times" /></span>
                      </div>
                      <span className="mx-2 text-muted">~</span>
                      <div className="input-group ms-2 w-auto">
                        <Field
                          name="NumberOfVisitTo"
                          type="text"
                          className="form-control"
                          regex={integerRegex}
                          as={FormikFormInputForRegexCheck}
                          placeholder="to"
                        />
                        <span className="input-group-text"><FormattedMessage id="label_times" /></span>
                      </div>
                    </div>

                    </div>

                    <div className="col-12 form-group">
                      <Label
                        className="label-style"
                        label="reason_for_visiting"
                      />
                      <Field
                        name="ReasonOfVisiting"
                        className="form-control"
                        as={FormikFormSelect}
                        options={props.motivationOptions}
                      />
                    </div>
                  </div>
                </Modal.Body>

                {/* Footer Buttons */}
                <div className="modal-footer center-content">
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={props.onModalClosed}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    <FormattedMessage id="close" />
                  </Button>
                  <LoaderButton
                    type="submit"
                    iconName="bi-check-lg"
                    disabled={_.isEqual(props.initialValues, values)}
                    isLoading={props.getDataLoading}
                    text="apply"
                  />
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default AdvanceSearchCustomerForm;
